import { useState } from "react";
import { useDataProvider, useNotify, useRefresh } from "react-admin";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";

/**
 * Upserts the policy-period-scoped claimant Note (US 5273 / ADR-052 §10) via
 * POST /claimants/{claimantId}/policy-notes/{policyId}. On success it refreshes
 * so views computed from the claimant record (Claims Summary, claimant page)
 * reflect the new note. The note is stored in the dedicated claimant_policy_notes
 * table — never the synced policy_data JSONB — so it survives reloads.
 */
export const useUpsertClaimantPolicyNote = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const refresh = useRefresh();
  const [saving, setSaving] = useState(false);

  const saveNote = async (claimantId: string, policyId: string, note: string): Promise<boolean> => {
    setSaving(true);
    try {
      await dataProvider.invoke(`claimants/${claimantId}/policy-notes/${policyId}`, { data: { note } });
      notify("Note saved", { type: "success" });
      refresh();
      return true;
    } catch (error) {
      notifyError(error);
      return false;
    } finally {
      setSaving(false);
    }
  };

  return { saveNote, saving };
};
